/* Offline drill-item cache (E7 2a).
 *
 * Caches the most recent NORMAL-mode drill session per module in IndexedDB so a
 * drill still loads offline. `fetchPackSession` (lib/api.ts) writes here on a
 * successful fetch and reads it back on a transient/offline failure.
 *
 * Safe to cache: drills grade CLIENT-SIDE, so their served items already carry the
 * answer key — persisting them client-side is no different from the network
 * response the client already holds. (Practice-test items are answer-stripped and
 * graded server-side; they are never cached here.) Weakness/review modes depend on
 * live server state, so only normal-mode sessions are cached.
 *
 * Dependency-free; degrades to a no-op when indexedDB is unavailable (tests/SSR),
 * so it can never throw or block a render. Mirrors lib/offline-queue.ts.
 */
import type { PackSession } from "./api.js";

const DB_NAME = "bht-pack-cache";
const STORE = "sessions";
const DB_VERSION = 1;

function hasIDB(): boolean {
  // The ACCESS must be inside the try, not just the use. When a browser blocks
  // site data (Safari private browsing, third-party-blocked embeds), reading
  // `indexedDB` is a getter that throws SecurityError, and `typeof` only
  // suppresses ReferenceError for an unresolvable name — this one resolves, so
  // the guard was itself the throw site. Production saw exactly that on
  // /blog/ pages: "SecurityError: The operation is insecure." from our own
  // bundle, escaping the caller's try because hasIDB() runs before it.
  // flags.ts and theme.ts already do it this way for localStorage.
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  // Same page-lifetime poisoning as offline-queue: a rejected promise cached in
  // a module variable disables pack caching until reload. Here it had no reset
  // attempt at all.
  const p = new Promise<IDBDatabase>((resolve, reject) => {
    // open() throws synchronously in the same blocked-storage contexts rather
    // than rejecting, so it needs its own guard even once hasIDB() is safe.
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      reject(e instanceof Error ? e : new Error("indexedDB.open failed"));
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "module" });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // Drop a CLOSED connection from the cache. The browser closes it on its own
      // account (storage pressure, a service-worker update, another tab upgrading
      // the schema) and none of that goes through the open() error path, so the
      // p.catch() below never fires and the dead handle is reused forever.
      db.onclose = () => {
        if (dbPromise === p) dbPromise = null;
      };
      db.onversionchange = () => {
        if (dbPromise === p) dbPromise = null;
        db.close();
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error("indexedDB.open errored"));
  });
  dbPromise = p;
  p.catch(() => {
    if (dbPromise === p) dbPromise = null;
  });
  return p;
}

function request<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        // db.transaction() THROWS synchronously when the connection is closing.
        // Unguarded, that rejection reached the page as an unhandledrejection on
        // /app/drill/slice: every occurrence of "Failed to execute 'transaction'
        // on 'IDBDatabase': The database connection is closing" came from here,
        // because a drill loads its pack through this cache. Forget the dead
        // handle as well as rejecting, or the next call throws identically.
        let tx: IDBTransaction;
        try {
          tx = db.transaction(STORE, mode);
        } catch (e) {
          dbPromise = null;
          reject(e instanceof Error ? e : new Error("transaction failed"));
          return;
        }
        const req = fn(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

type CachedSession = { module: string; session: PackSession; ts: number };

/** Persist the latest normal-mode session for a module (best-effort). */
export async function cachePackSession(module: string, session: PackSession): Promise<void> {
  if (!hasIDB()) return;
  try {
    await request("readwrite", (s) =>
      s.put({ module, session, ts: Date.now() } satisfies CachedSession),
    );
  } catch {
    /* cache write is best-effort; never surface to the drill */
  }
}

/** The last cached normal-mode session for a module, or null. */
export async function getCachedPackSession(module: string): Promise<PackSession | null> {
  if (!hasIDB()) return null;
  try {
    const rec = await request<CachedSession | undefined>("readonly", (s) => s.get(module));
    return rec?.session ?? null;
  } catch {
    return null;
  }
}
